/**
 * ThankYouOverlay — Success animation shown after feedback is submitted.
 *
 * Displays a brief "thank you" message with a checkmark animation.
 * Auto-dismisses after a configurable duration.
 */
import { useEffect } from "react";

interface ThankYouOverlayProps {
  readonly issueCount: number;
  readonly onDismiss: () => void;
  /** Auto-dismiss delay in milliseconds. Defaults to 3000. */
  readonly dismissDelay?: number;
}

export function ThankYouOverlay({
  issueCount,
  onDismiss,
  dismissDelay = 3000,
}: ThankYouOverlayProps) {
  useEffect(() => {
    const timer = setTimeout(onDismiss, dismissDelay);
    return () => clearTimeout(timer);
  }, [onDismiss, dismissDelay]);

  return (
    <div className="fb-thankyou-overlay" role="status" aria-live="polite">
      <div className="fb-thankyou-content">
        {/* Animated checkmark */}
        <div className="fb-thankyou-check" aria-hidden="true">
          <svg
            width="64"
            height="64"
            viewBox="0 0 64 64"
            fill="none"
            className="fb-thankyou-svg"
          >
            <circle
              cx="32"
              cy="32"
              r="30"
              stroke="currentColor"
              strokeWidth="3"
              className="fb-thankyou-circle"
            />
            <path
              d="M20 33 L28 41 L44 23"
              stroke="currentColor"
              strokeWidth="3"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="fb-thankyou-tick"
            />
          </svg>
        </div>

        <h3 className="fb-thankyou-heading">Thank You!</h3>
        <p className="fb-thankyou-message">
          {issueCount === 1
            ? "Your feedback has been submitted successfully."
            : `Your ${issueCount} feedback items have been submitted successfully.`}
        </p>
        <p className="fb-thankyou-sub">
          Your input helps us make RetireEZ better for everyone.
        </p>
      </div>
    </div>
  );
}
